<template>
  <!-- ADMIN ONLY. The panel a dot on the "Public photos" overlay opens: the
       photo(s) under the click, what we know about each, and links to the
       recording or plan it is on and to whoever uploaded it.

       The POI sheet's shell (app.scss .poi-sheet__*), so a photo opens where a
       POI does and reads the same: a bottom sheet on a phone, a panel at the
       map's top-right from 992px. Rendered by PublicPhotosMapHost, which owns
       the fetch and where the panel sits. -->
  <div v-if="open" class="photo-sheet">
    <div
      class="poi-sheet__dialog photo-sheet__dialog"
      :style="panelStyle"
      role="dialog"
      aria-modal="false"
      :aria-label="$t('admin_public_photos.dialog.title')"
      data-photo-sheet
    >
      <div class="poi-sheet__content">
        <div class="poi-sheet__handle" aria-hidden="true"></div>

        <header class="poi-sheet__header">
          <span class="poi-chip">
            <span class="material-icons">photo_camera</span>
            {{ $t('admin_public_photos.dialog.title') }}
            <span class="photo-sheet__admin-badge">{{ $t('admin_public_photos.dialog.admin_only') }}</span>
          </span>

          <div class="poi-sheet__tools">
            <a
              v-if="current"
              class="poi-icon-btn"
              :href="current.adminUrl"
              target="_blank"
              rel="noopener noreferrer"
              :aria-label="$t('admin_public_photos.dialog.open_in_admin')"
              :title="$t('admin_public_photos.dialog.open_in_admin')"
              data-test="photo-admin-link"
            >
              <span class="material-icons">admin_panel_settings</span>
            </a>
            <button
              type="button"
              class="poi-icon-btn"
              :aria-label="$t('shared.close')"
              data-test="photo-close"
              @click="$emit('close')"
            >
              <span class="material-icons">close</span>
            </button>
          </div>
        </header>

        <div class="poi-sheet__body">
          <div v-if="state === 'loading'" class="photo-sheet__status" role="status" data-test="photo-loading">
            <span class="spinner-border spinner-border-sm" aria-hidden="true"></span>
            <span>{{ $t('shared.loading') }}</span>
          </div>

          <p v-else-if="state === 'gone'" class="photo-sheet__status" role="status" data-test="photo-gone">
            <span class="material-icons" aria-hidden="true">visibility_off</span>
            {{ $t('admin_public_photos.dialog.no_longer_public') }}
          </p>

          <p v-else-if="state === 'denied'" class="photo-sheet__status" role="alert" data-test="photo-denied">
            <span class="material-icons" aria-hidden="true">lock</span>
            <span>
              {{ $t('admin_public_photos.dialog.admin_check_needed') }}
              <a href="/admin/two_factor" target="_blank" rel="noopener noreferrer">{{ $t('admin_public_photos.dialog.verify_admin') }}</a>
            </span>
          </p>

          <p v-else-if="state === 'failed'" class="photo-sheet__status" role="alert" data-test="photo-error">
            <span class="material-icons" aria-hidden="true">error_outline</span>
            {{ $t('admin_public_photos.dialog.error') }}
          </p>

          <template v-else-if="current">
            <!-- The carousel: one photo at a time; the arrows step through the
                 ones stacked under the click. Tapping it opens the :hd. -->
            <div class="photo-carousel" data-test="photo-carousel">
              <button
                v-if="current.photo && !imageFailed[current.id]"
                type="button"
                class="photo-carousel__frame"
                :aria-label="$t('admin_public_photos.dialog.open_full_size')"
                @click="lightboxOpen = true"
              >
                <img
                  :key="current.id"
                  :src="current.photo.thumbUrl"
                  :alt="$t('admin_public_photos.dialog.photo_alt', { id: current.id })"
                  data-test="photo-image"
                  @error="imageFailed = { ...imageFailed, [current.id]: true }"
                />
              </button>
              <!-- No file yet, or one that won't load (an object missing from the
                   bucket: some pre-R2 blobs are). -->
              <div v-else class="photo-carousel__frame photo-carousel__frame--empty" data-test="photo-no-file">
                <span class="material-icons" aria-hidden="true">hide_image</span>
                <small v-if="current.photo">{{ $t('admin_public_photos.dialog.image_failed') }}</small>
                <small v-else>{{ $t('admin_public_photos.dialog.file_not_ready') }}</small>
              </div>

              <template v-if="views.length > 1">
                <button
                  type="button"
                  class="photo-carousel__nav photo-carousel__nav--prev"
                  :aria-label="$t('utils.detail_sheet_lightbox.previous_photo')"
                  data-test="photo-prev"
                  @click="step(-1)"
                >
                  <span class="material-icons">chevron_left</span>
                </button>
                <button
                  type="button"
                  class="photo-carousel__nav photo-carousel__nav--next"
                  :aria-label="$t('utils.detail_sheet_lightbox.next_photo')"
                  data-test="photo-next"
                  @click="step(1)"
                >
                  <span class="material-icons">chevron_right</span>
                </button>
                <span class="photo-carousel__counter" data-test="photo-counter">{{ index + 1 }} / {{ views.length }}</span>
              </template>
            </div>

            <h2 class="poi-sheet__title" data-test="photo-title">{{ current.parent ? current.parent.text : $t('admin_public_photos.dialog.title') }}</h2>
            <p class="poi-sheet__source">{{ current.parent ? current.parent.label : '' }}</p>

            <div class="poi-pills">
              <span v-if="elevationLabel" class="poi-pill">{{ elevationLabel }}</span>
              <a v-if="current.coordsText" class="poi-pill" :href="current.coordsHref">{{ current.coordsText }}</a>
            </div>

            <ul class="poi-contact" data-test="photo-links">
              <li v-if="current.parent">
                <span class="material-icons">route</span>
                <a v-if="current.parent.href" :href="current.parent.href" target="_blank" rel="noopener noreferrer" data-test="photo-parent-link">{{ current.parent.text }}</a>
                <span v-else>{{ current.parent.text }}</span>
                <a
                  v-if="current.parent.adminHref"
                  class="photo-sheet__admin-link"
                  :href="current.parent.adminHref"
                  target="_blank"
                  rel="noopener noreferrer"
                  data-test="photo-parent-admin-link"
                >{{ $t('admin_public_photos.dialog.admin') }}</a>
              </li>
              <li v-if="current.uploader">
                <span class="material-icons">person</span>
                <a v-if="current.uploader.adminHref" :href="current.uploader.adminHref" target="_blank" rel="noopener noreferrer" data-test="photo-uploader-link">{{ current.uploader.text }}</a>
                <span v-else>{{ current.uploader.text }}</span>
              </li>
            </ul>

            <DetailSheetSection>
              <DetailSheetMetaRow v-for="row in current.rows" :key="row.key" :label="row.label" :value="row.value" />
            </DetailSheetSection>
          </template>
        </div>
      </div>
    </div>

    <DetailSheetLightbox
      :open="lightboxOpen && !!current && !!current.photo && !imageFailed[current.id]"
      :photos="current && current.photo ? [current.photo] : []"
      :index="0"
      @close="lightboxOpen = false"
    />
  </div>
</template>

<script lang="ts">
import { defineComponent } from 'vue';
import type { PropType } from 'vue';
import DetailSheetLightbox from '@/utils/shared_components/detail_sheet/DetailSheetLightbox.vue';
import DetailSheetMetaRow from '@/utils/shared_components/detail_sheet/DetailSheetMetaRow.vue';
import DetailSheetSection from '@/utils/shared_components/detail_sheet/DetailSheetSection.vue';
import { formatElevation } from '@/utils/helpers/poiSheet';
import { stepIndex } from '@/utils/publicPhotos/publicPhotoDetails';
import type { PublicPhotoView } from '@/utils/publicPhotos/publicPhotoDetails';

export type PhotoSheetState = 'loading' | 'shown' | 'gone' | 'denied' | 'failed';

export default defineComponent({
  name: 'MapPhotoDetailDialog',
  components: { DetailSheetLightbox, DetailSheetMetaRow, DetailSheetSection },
  props: {
    open: { type: Boolean, default: false },
    state: { type: String as PropType<PhotoSheetState>, default: 'loading' },
    views: { type: Array as PropType<PublicPhotoView[]>, default: () => [] },
    panelStyle: { type: Object as PropType<Record<string, string> | null>, default: null },
  },
  emits: ['close'],
  data() {
    return { index: 0, lightboxOpen: false, imageFailed: {} as Record<string, boolean> };
  },
  computed: {
    current(): PublicPhotoView | null {
      return this.views[this.index] ?? null;
    },
    elevationLabel(): string {
      return formatElevation(this.current?.elevationMetres ?? null);
    },
  },
  watch: {
    // A new click: back to its first photo.
    views() {
      this.index = 0;
      this.lightboxOpen = false;
      this.imageFailed = {};
    },
    open(isOpen: boolean) {
      if (!isOpen) this.lightboxOpen = false;
    },
  },
  methods: {
    step(delta: number) {
      this.index = stepIndex(this.index, delta, this.views.length);
    },
  },
});
</script>

<style scoped>
/* Above the POI sheet (a Bootstrap modal, 1055) and level with the hazard
   sheet, so a photo opened over a POI reads on top; below the lightbox (2000). */
.photo-sheet__dialog {
  z-index: 1056;
}

.photo-sheet__admin-badge {
  margin-left: 0.25rem;
  padding: 0 0.4rem;
  border-radius: 999px;
  background: rgba(233, 30, 140, 0.2);
  color: #f48fc5;
  font-size: 0.65rem;
  font-weight: 700;
  letter-spacing: 0.04em;
  text-transform: uppercase;
}

.photo-sheet__status {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin: 0.5rem 0 1rem;
  padding: 0.75rem;
  border-radius: 12px;
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.08);
  color: #a1a4ad;
}

.photo-sheet__status a {
  color: #fddf00;
}

.photo-sheet__admin-link {
  margin-left: auto;
  font-size: 0.75rem;
  color: #a1a4ad !important;
}

.photo-carousel {
  position: relative;
  margin-bottom: 0.75rem;
}

.photo-carousel__frame {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  aspect-ratio: 4 / 3;
  padding: 0;
  border: 0;
  border-radius: 10px;
  overflow: hidden;
  background: rgba(255, 255, 255, 0.05);
  cursor: zoom-in;
}

.photo-carousel__frame img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.photo-carousel__frame--empty {
  flex-direction: column;
  gap: 0.25rem;
  color: #a1a4ad;
  cursor: default;
}

.photo-carousel__nav {
  position: absolute;
  top: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  padding: 0;
  border: 0;
  border-radius: 50%;
  background: rgba(0, 0, 0, 0.6);
  color: #fff;
  transform: translateY(-50%);
}

.photo-carousel__nav--prev { left: 8px; }
.photo-carousel__nav--next { right: 8px; }

.photo-carousel__counter {
  position: absolute;
  right: 8px;
  bottom: 8px;
  padding: 0.1rem 0.5rem;
  border-radius: 999px;
  background: rgba(0, 0, 0, 0.6);
  color: #fff;
  font-size: 0.75rem;
}
</style>
